import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | undefined} */
  #operationTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #hiddenByOverlay = false;

  /** @type {Set<Element>} */
  #bottomBlockersInView = new Set();

  /** @type {number | null} */
  #bottomBlockerFrame = null;

  /** @type {Set<number>} */
  #selectionSyncTimeouts = new Set();

  /** @type {MutationObserver | null} */
  #kachingMountObserver = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    // The storefront scrolls inside .page-wrapper, and review widgets may
    // arrive after this component connects. Recheck live geometry on every
    // relevant scroll state so hash links and late app renders cannot leave
    // the sticky bar covering proof or support content.
    const scrollRoot = document.querySelector('.page-wrapper');
    scrollRoot?.addEventListener('scroll', this.#scheduleBottomBlockerCheck, { passive: true, signal });
    window.addEventListener('scroll', this.#scheduleBottomBlockerCheck, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleBottomBlockerCheck, { passive: true, signal });
    window.requestAnimationFrame(this.#scheduleBottomBlockerCheck);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('theme-drawer:open', this.#handleDrawerOpen, { signal });
    document.addEventListener('theme-drawer:close', this.#handleDrawerClose, { signal });
    document.addEventListener('click', this.#handleKachingInteraction, { capture: true, signal });
    document.addEventListener('change', this.#handleKachingInteraction, { capture: true, signal });

    this.#getInitialQuantity();
    this.#watchForKachingMount(target);
    [0, 300, 900, 1800, 3500].forEach((delay) => this.#queueKachingSync(delay));

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#kachingMountObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
    if (this.#operationTimeout) {
      clearTimeout(this.#operationTimeout);
    }
    if (this.#bottomBlockerFrame) {
      cancelAnimationFrame(this.#bottomBlockerFrame);
    }
    this.#selectionSyncTimeouts.forEach((timeout) => window.clearTimeout(timeout));
    this.#selectionSyncTimeouts.clear();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    const bottomBlockers = [
      footer,
      ...document.querySelectorAll('#product-reviews, .np5-pdp__chooser, .np8-pdp__set-value, .np5-pdp__faq'),
    ];

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#hasVisibleBottomBlocker()) {
            this.#hiddenByBottom = true;
            return;
          }
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#bottomBlockersInView.add(entry.target);
          } else {
            this.#bottomBlockersInView.delete(entry.target);
          }
        });

        if (this.#bottomBlockersInView.size > 0 && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (this.#bottomBlockersInView.size === 0 && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    bottomBlockers.forEach((element) => this.#mainBottomObserver?.observe(element));
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) return;
    const targetButton = this.#targetAddToCartButton;
    const form = targetButton.closest('form');
    if (!form?.checkValidity()) {
      form?.reportValidity();
      return;
    }

    const productForm = /** @type {any} */ (targetButton.closest('product-form-component'));
    const quantitySelector =
      productForm?.refs?.quantitySelector ?? productForm?.querySelector('quantity-selector-component');
    const validation = quantitySelector?.canAddToCart?.();
    if (validation && !validation.canAdd) {
      targetButton.click();
      return;
    }

    const stickyButton = this.refs.addToCartButton;
    stickyButton.removeAttribute('data-added');
    stickyButton.dataset.pending = 'true';
    stickyButton.setAttribute('aria-busy', 'true');
    targetButton.dataset.puppet = 'true';

    if (this.#operationTimeout) clearTimeout(this.#operationTimeout);
    this.#operationTimeout = window.setTimeout(() => {
      this.#clearStickyPending();
      targetButton.dataset.puppet = 'false';
    }, 16000);

    targetButton.click();

    // If native validation or another synchronous guard prevented submission,
    // the target remains enabled and no cart event will arrive to clear state.
    window.setTimeout(() => {
      if (targetButton.dataset.puppet === 'true' && !targetButton.disabled) {
        this.#clearStickyPending();
        targetButton.dataset.puppet = 'false';
      }
    }, 0);
  };

  #clearStickyPending() {
    if (this.#operationTimeout) {
      clearTimeout(this.#operationTimeout);
      this.#operationTimeout = undefined;
    }
    const stickyButton = this.refs.addToCartButton;
    stickyButton.removeAttribute('data-pending');
    stickyButton.removeAttribute('aria-busy');
  }

  async #showStickySuccess() {
    const stickyButton = this.refs.addToCartButton;
    const productImage = this.refs.productImage;
    const cartIcon = document.querySelector('.np5-header__cart-icon, .header-actions__cart-icon');

    this.#clearStickyPending();
    stickyButton.dataset.added = 'true';

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      stickyButton.removeAttribute('data-added');
    }, 2600);

    if (!cartIcon || !productImage) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);

    await onAnimationEnd(flyToCartElement);
    flyToCartElement.remove();
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    const initiatedBySticky = this.#targetAddToCartButton?.dataset.puppet === 'true';
    if (!initiatedBySticky) return;

    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise
        .then(({ detail }) => {
          if (detail?.didError) this.#clearStickyPending();
          else this.#showStickySuccess();
        })
        .catch(() => this.#clearStickyPending())
        .finally(resetPuppet);
    } else {
      this.#clearStickyPending();
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Keeps the sticky purchase summary aligned with the Kaching option that
   * will be added by the main product form.
   *
   * @param {Event} event
   */
  #handleKachingInteraction = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.kaching-bundles__bar')) return;
    this.#queueKachingSync(0);
    this.#queueKachingSync(120);
    this.#queueKachingSync(320);
  };

  /**
   * @param {number} delay
   */
  #queueKachingSync(delay) {
    const timeout = window.setTimeout(() => {
      this.#selectionSyncTimeouts.delete(timeout);
      this.#syncKachingSelection();
    }, delay);
    this.#selectionSyncTimeouts.add(timeout);
  }

  /**
   * Kaching can mount after the initial timed sync window on slow connections.
   * Keep a component-scoped observer until a real selected offer exists, then
   * run the same settling passes used for direct customer interaction.
   *
   * @param {Element | null} section
   */
  #watchForKachingMount(section) {
    if (!section) return;

    const syncWhenReady = () => {
      const bars = section.querySelector('.kaching-bundles__bars');
      const selected =
        bars?.querySelector('input[type="radio"]:checked')?.closest('.kaching-bundles__bar') ??
        bars?.querySelector('.kaching-bundles__bar--selected');
      if (!selected) return false;

      this.#queueKachingSync(0);
      this.#queueKachingSync(120);
      this.#queueKachingSync(320);
      this.#kachingMountObserver?.disconnect();
      this.#kachingMountObserver = null;
      return true;
    };

    if (syncWhenReady()) return;
    this.#kachingMountObserver = new MutationObserver(syncWhenReady);
    this.#kachingMountObserver.observe(section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'checked'],
    });
  }

  #syncKachingSelection() {
    const section = this.closest('.shopify-section');
    const bars = section?.querySelector('.kaching-bundles__bars');
    if (!bars) return;

    const checkedInput = bars.querySelector('input[type="radio"]:checked');
    const selected =
      checkedInput?.closest('.kaching-bundles__bar') ??
      bars.querySelector('.kaching-bundles__bar--selected');
    if (!selected) return;

    const optionTitle = selected.querySelector('.kaching-bundles__bar-title')?.textContent.trim() ?? '';
    const optionPrice = selected.querySelector('.kaching-bundles__bar-price')?.textContent.trim() ?? '';
    const isCompleteRoutine = selected.classList.contains('np5-kaching-option--complete');
    const baseTitle = this.dataset.np5BaseTitle ?? '';
    const setTitle = this.dataset.np5SetTitle ?? baseTitle;
    const title = isCompleteRoutine ? setTitle : optionTitle ? `${baseTitle} · ${optionTitle}` : baseTitle;
    const titleElement = this.querySelector('.sticky-add-to-cart__title');
    const priceElement = this.querySelector('.sticky-add-to-cart__price');

    if (titleElement && title) titleElement.textContent = title;
    if (priceElement && optionPrice) priceElement.textContent = optionPrice;

    const imageUrl = isCompleteRoutine ? this.dataset.np5SetImageUrl : this.dataset.np5BaseImageUrl;
    if (this.refs.productImage && imageUrl) {
      this.refs.productImage.removeAttribute('srcset');
      this.refs.productImage.removeAttribute('sizes');
      this.refs.productImage.src = imageUrl;
      this.refs.productImage.alt = isCompleteRoutine ? setTitle : baseTitle;
    }

    const quantityMatch = optionTitle.match(/\b(\d+)\b/);
    this.#currentQuantity = isCompleteRoutine ? 1 : Number.parseInt(quantityMatch?.[1] ?? '1', 10) || 1;
    this.#updateButtonText();
    this.dataset.np5SelectedOffer = isCompleteRoutine ? 'complete-routine' : optionTitle;
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#hiddenByOverlay || document.querySelector('theme-drawer[open]')) {
      this.#hiddenByOverlay = true;
      return;
    }
    if (this.#hasVisibleBottomBlocker()) {
      this.#hiddenByBottom = true;
      return;
    }
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #handleDrawerOpen = () => {
    this.#hiddenByOverlay = true;
    this.#hideStickyBar();
  };

  #handleDrawerClose = () => {
    window.requestAnimationFrame(() => {
      if (document.querySelector('theme-drawer[open]')) return;

      this.#hiddenByOverlay = false;
      const productForm = this.#getProductForm();
      const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
      const rect = buyButtonsBlock?.getBoundingClientRect();

      if (rect && (rect.bottom < 0 || rect.top < 0) && !this.#hasVisibleBottomBlocker()) {
        this.#showStickyBar();
      }
    });
  };

  /**
   * Returns true when proof, support, or footer content occupies the viewport.
   * Querying on demand also covers third-party review markup inserted after
   * the initial IntersectionObserver was configured.
   *
   * @returns {boolean}
   */
  #hasVisibleBottomBlocker() {
    const blockers = document.querySelectorAll(
      '#product-reviews, .np5-pdp__chooser, .np8-pdp__set-value, .np5-pdp__faq, footer, [class*="footer-group"]'
    );

    return [...blockers].some((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      const styles = getComputedStyle(element);
      if (styles.display === 'none' || styles.visibility === 'hidden') return false;
      return rect.top < window.innerHeight && rect.bottom > 0;
    });
  }

  #scheduleBottomBlockerCheck = () => {
    if (this.#bottomBlockerFrame) return;

    this.#bottomBlockerFrame = window.requestAnimationFrame(() => {
      this.#bottomBlockerFrame = null;
      const blockerVisible = this.#hasVisibleBottomBlocker();

      if (blockerVisible) {
        this.#hiddenByBottom = true;
        if (this.#isStuck) this.#hideStickyBar();
        return;
      }

      if (!this.#hiddenByBottom) return;
      const productForm = this.#getProductForm();
      const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
      const rect = buyButtonsBlock?.getBoundingClientRect();
      this.#hiddenByBottom = false;

      if (rect && (rect.bottom < 0 || rect.top < 0)) {
        this.#showStickyBar();
      }
    });
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
